import React from 'react';

import { Button, Typography } from '@growdash/design-system/atoms';
import BookMarkIcon from '@growdash/growintel/assets/icons/bookmark';
import GrowintelImage from '@growdash/growintel/components/atoms/growintel-image';
import { Brand } from '@growdash/growintel/generated';
import useCurrency from '@growdash/growintel/hooks/useCurrency';
import { useGrowintelService } from '@growdash/growintel/hooks/useGrowintelService';
import { Loader2, UtensilsCrossed } from 'lucide-react';

interface BrandCardProps {
  brand?: Brand;
  isEmptyState?: boolean;
  color?: 'purple' | 'orange';
  className?: string;
  refetchWatchlist?: () => void;
  isWatchlisted?: (brandId?: string) => boolean;
}

const colors = {
  purple: {
    card: 'bg-gradient-purple ',
    borderContainer: 'bg-gradient-purple-border p-[0.0625rem]',
    innerContainer: 'rounded-[0.9375rem]',
    saveButton: `text-bg_accent hover:text- hover:bg-purple-500/5 border-bg_accent bg-transparent [data-loading='true']:bg-orange:500/20 [data-loading='true']:pointer-events-none`,
    unSaveButton: `bg-bg_accent text-white [data-loading='true']:bg-orange:500/20 hover:bg- hover:text- hover:opacity-80 border-bg_accent`,
    emptyIcon: 'bg-[#E3D6FE] dark:bg-[#5D4C8F]',
  },

  orange: {
    card: 'bg-gradient-orange',
    borderContainer:
      'bg-gradient-orange-border shadow-[0px_4px_8px_0px_#B4B0B03B] p-[0.09375rem]',
    innerContainer: 'rounded-[0.90625rem]',
    saveButton: `text-support-orange hover:text- hover:bg-orange-500/5 border-support-orange bg-transparent [data-state='loading']:bg-orange:500/20`,
    unSaveButton: `bg-support-orange text-white [data-state='loading']:bg-orange:500/20 hover:bg- hover:text- hover:opacity-80 border-support-orange`,
    emptyIcon: 'bg-[#FED6C2] dark:bg-[#8A4B3C]',
  },
};

export default function BrandCard({
  brand,
  isEmptyState = false,
  color = 'purple',
  className = '',
  refetchWatchlist,
  isWatchlisted,
}: BrandCardProps) {
  const { brand_name, cuisines, avg_price, avg_rating_score, brand_image } =
    brand ?? {};

  const [watchlistLoading, setWatchlistLoading] = React.useState(false);

  const service = useGrowintelService();

  const [watchlisted, setWatchlisted] = React.useState(
    isWatchlisted ? isWatchlisted(brand?.id ?? '') : false,
  );

  const handleAddToWatchlist = async () => {
    try {
      if (!brand?.id) return;
      setWatchlistLoading(true);
      const response =
        await service?.watchlistAPI.addBrandToWatchlistWatchlistBrandIdPost(
          brand.id,
        );
      if (response && response.status === 201) {
        setWatchlisted(true);
        refetchWatchlist?.();
      }
    } catch (error) {
      console.error('Failed to add brand to watchlist:', error);
    } finally {
      setWatchlistLoading(false);
    }
  };

  const handleRemoveFromWatchlist = async () => {
    try {
      if (!brand?.id) return;
      setWatchlistLoading(true);
      const response =
        await service?.watchlistAPI.removeBrandFromWatchlistWatchlistBrandIdDelete(
          brand.id,
        );
      if (response && response.status === 200) {
        setWatchlisted(false);
        refetchWatchlist?.();
      }
    } catch (error) {
      console.error('Failed to remove brand from watchlist:', error);
    } finally {
      setWatchlistLoading(false);
    }
  };
  const styleConfig = colors[color];

  const currency = useCurrency();

  return (
    <div
      className={`${className} overflow-hidden rounded-2xl animate-fade-in flex items-center justify-center ${styleConfig.borderContainer}`}
    >
      <div
        className={`w-full h-full bg-background rounded-[0.90625rem] overflow-hidden ${styleConfig.innerContainer}`}
      >
        <div className={` ${styleConfig.card} w-full h-full`}>
          <div className="relative p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className={`relative h-6 w-6 rounded-full overflow-hidden flex items-center justify-center ${styleConfig.emptyIcon}`}
                >
                  {brand_image ? (
                    <GrowintelImage
                      src={brand_image}
                      className="w-full h-full"
                    />
                  ) : (
                    <svg
                      width="11"
                      height="12"
                      viewBox="0 0 11 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6.15291 0H0.117083V3.74874H4.78377L0.367568 8.74227C0.157676 8.9796 0.157676 9.33618 0.367568 9.57351L2.04356 11.4686C2.29325 11.7509 2.73377 11.7509 2.98347 11.4686L7.65766 6.18336V11.8879H8.4905C9.87646 11.8879 11 10.7644 11 9.37845V8.32216C11 7.40352 10.6641 6.51658 10.0555 5.82844L9.34505 5.02512C9.13515 4.78779 9.13515 4.43121 9.34505 4.19388L10.3666 3.03873C10.5765 2.8014 10.5765 2.44482 10.3666 2.20749L9.91255 1.69403C8.95989 0.616825 7.59094 0 6.15291 0Z"
                        fill="white"
                      />
                    </svg>
                  )}
                </div>
                <Typography className="text-lg font-bold">
                  {brand_name ?? 'Select to compare'}
                </Typography>
              </div>

              {brand && (
                <Button
                  variant="outline"
                  onClick={
                    watchlisted
                      ? handleRemoveFromWatchlist
                      : handleAddToWatchlist
                  }
                  data-loading={watchlistLoading ? 'true' : 'false'}
                  className={`h-9 text-[0.8125rem] font-semibold px-2
                    ${isEmptyState ? 'hidden' : ''}
                    ${
                      watchlisted
                        ? styleConfig.unSaveButton
                        : styleConfig.saveButton
                    }
                    `}
                >
                  {watchlistLoading ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <BookMarkIcon
                      className={`cursor-pointer animate-fade-in
                    ${watchlisted ? 'fill-white' : ''}
                    ${isEmptyState ? 'hidden' : ''}
                    `}
                    />
                  )}
                  {watchlistLoading && !watchlisted && 'Saving to watchlist...'}
                  {watchlistLoading && watchlisted && 'Removing...'}
                  {!watchlistLoading && watchlisted && 'In watchlist'}
                  {!watchlistLoading && !watchlisted && 'Save'}
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2 mt-4">
              <UtensilsCrossed size={12} className="min-w-3 text-quarternary" />
              {!isEmptyState ? (
                <div className="flex flex-wrap gap-2">
                  {cuisines?.split(',').map((cuisine) => (
                    <div
                      key={cuisine}
                      className="py-1 px-1.5 bg-tertiary border border-tertiary rounded-[0.25rem]"
                    >
                      <Typography className="text-tertiary font-semibold text-xs tracking-[-0.01em]">
                        {cuisine.trim()}
                      </Typography>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <Typography className="font-semibold text-xs text-tertiary">
                    Select a brand to compare the cuisine
                  </Typography>
                </div>
              )}
            </div>

            <div
              className={`grid grid-cols-2 gap-4 pt-4 ${
                isEmptyState ? 'hidden' : ''
              }`}
            >
              <div className="bg-card rounded-2xl p-4 border border-tertiary">
                <Typography className="text-tertiary text-xs font-semibold">
                  Average price
                </Typography>
                <div className="h-[1.875rem] flex items-center">
                  <Typography className="font-bold text-primary">
                    {avg_price ? `${avg_price.toFixed(2)} ${currency}` : '-'}
                  </Typography>
                </div>
              </div>

              <div className="bg-card rounded-2xl p-4 border border-tertiary">
                <Typography className="text-tertiary text-xs font-semibold">
                  Average rating
                </Typography>
                <div className="h-[1.875rem] flex items-center">
                  <Typography className="font-bold text-primary">
                    {avg_rating_score !== null && avg_rating_score !== undefined
                      ? Math.max(0, avg_rating_score).toFixed(1)
                      : '-'}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
